<template>
  <section
    class="position-relative hero-bg-image-container d-flex flex-column justify-content-end"
    :style="bgImage"
    :class="sectionClass">
    <div :style="bgGradientStyle">
      <div class="container px-0">
        <div class="col-12 px-0">
          <div class="hero-image-container" :class="textWrapperClasses">
            <slot name="above-title"></slot>
            <component :is="titleLevel" :class="titleClass">
              <span class="highlight-text" :class="titleHighlightStyle">
                {{ title }}
              </span>
            </component>
            <slot name="below-title"></slot>
            <div
              v-if="showText"
              :class="textClass"
              class="d-none d-md-block w-75 mt-md-space-xs">
              <div class="highlight-text" :class="textHighlightStyle">
                {{ text }}
              </div>
            </div>
            <slot name="below-text"></slot>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";

interface Props {
  fullWidth?: boolean;
  bgImageSource: string;
  displayGradient?: boolean;
  gradientOpacity?: number;
  showText?: boolean;
  text?: string;
  textFontWeight?: "bold" | "normal" | "light" | "lighter";
  textSize?: string;
  textVariant?: string;
  showTextHighlight?: boolean;
  textHighlightVariant?: string;
  title: string;
  titleLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  titleSize?: string;
  titleVariant?: string;
  showTitleHighlight?: boolean;
  titleHighlightVariant?: string;
}

const props = withDefaults(defineProps<Props>(), {
  fullWidth: true,
  displayGradient: true,
  gradientOpacity: 0.2,
  showText: false,
  text: `lorem ipsum`,
  textFontWeight: "normal",
  textSize: "large",
  textVariant: "white",
  showTextHighlight: false,
  textHighlightVariant: "dark-3",
  titleLevel: "h1",
  titleSize: "medium",
  titleVariant: "white",
  showTitleHighlight: false,
  titleHighlightVariant: "dark-3",
});

const bgImage = computed(() => {
  return { "background-image": `url('${props.bgImageSource}')` };
});

const sectionClass = computed(() => {
  return props.fullWidth ? "" : `container-xxl gx-0`;
});

const textWrapperClasses = computed(() => {
  let classes = "";

  props.fullWidth
    ? (classes += " py-space-md py-lg-space-lg gx-0 ")
    : (classes +=
        " px-space-xs px-md-space-md px-lg-space-xxl pt-space-xxl pb-space-md pb-lg-space-lg ");

  return classes;
});

const textClass = computed(() => {
  let result = `fs-${props.textSize} text-${props.textVariant} fw-${props.textFontWeight}`;

  return result;
});

const textHighlightStyle = computed(() => {
  return props.showTextHighlight
    ? `bg-${props.textHighlightVariant} px-space-xs py-space-xxs`
    : null;
});

const bgGradientStyle = computed(() => {
  if (props.displayGradient) {
    // mix gradient with gradientOpacity
    const color1 = "rgba(0,0,0,0)";
    const color2 =
      "rgba(0,0,0," + (0.745 * props.gradientOpacity).toString() + ")";
    const color3 = "rgba(0,0,0," + props.gradientOpacity.toString() + ")";

    return {
      background: `transparent linear-gradient(180deg, ${color1} 0%, ${color2} 28%, ${color3} 100%) 0% 0% no-repeat padding-box`,
    };
  } else {
    return null;
  }
});

const titleClass = computed(() => {
  return `${props.titleLevel}-${props.titleSize} text-${props.titleVariant}`;
});

const titleHighlightStyle = computed(() => {
  return props.showTitleHighlight
    ? `bg-${props.titleHighlightVariant} px-space-xs py-space-xxs`
    : null;
});
</script>

<style lang="scss" scoped>
.hero-bg-image-container {
  background-repeat: no-repeat;
  background-size: cover;
  background-position: top;
  min-height: 256px;
}
.highlight-text {
  display: inline;
  padding: 4px 0;
  -webkit-box-decoration-break: clone;
  -ms-box-decoration-break: clone;
  -o-box-decoration-break: clone;
  box-decoration-break: clone;
}

@media (min-width: 768px) {
  .hero-bg-image-container {
    min-height: 400px;
  }
}

@media (min-width: 992px) {
  .hero-bg-image-container {
    min-height: 512px;
  }
}
</style>
